import * as ser from '../Common/SerUtil.js'
import { ComponentArray } from '../Common/ComponentArray.js'
import { makeEnum, makeEnumWithData, makeOptions,
  makeEnumWithDataAndLabels,
  setupClass, lookupData, Matches,
  interpolateInMap, doubleInterpolateInMap,
  IdsMap, PleaseContactStr,
  IntervalTimer,
} from '../Base.js'
import { extendArray } from '../SharedUtils.js'
import { WindowsData, } from '../MaterialData/Windows/WindowsData.js' 
import { MaterialDataTable } from '../MaterialData/MaterialDataTable.js'
import { TimeSeriesDataTable } from '../MaterialData/TimeSeriesDataTable.js'
import {
  TimeSeriesData_Table,
  ActivityLevels_Table,
  LuminairesData_Table,
} from '../MaterialData/MaterialDataTableRegistry.js'
import { CalcContext } from '../Common/CalcContext.js'
import { IACDebugger } from '../Components/IACDebugger.js'
import { TypesSet } from '../Components/TypesSet.js'
import { GuideState } from './GuideState.js'

import { Project, ProjectTypes } from '../Project.js'

export { Units } from '../Common/Units.js'

import {
  ProjectUnits,
} from '../Common/Field.js'
export * from '../Common/Field.js'

import { DoorType } from '../Components/DoorType.js'
import { ExteriorShadingType } from '../Components/ExteriorShadingType.js'
import { InteriorShadingType } from '../Components/InteriorShadingType.js'
import { WindowType } from '../Components/WindowType.js'
import { WallType, RoofType } from '../Components/WallType.js'
import { SkylightType } from '../Components/SkylightType.js'
import { BufferSpaceType } from '../Components/BufferSpaceType.js'

import { BuildingAndEnv } from './BuildingAndEnv.js'
import { ComponentsLibrary } from './ComponentsLibrary.js'
import { System } from './System.js'
import { Space } from './Space.js'
import { Schedule } from './Schedule.js'

class CommercialDebugData {
  init(proj) {
    this.proj = proj

    this.iacDebugger = IACDebugger.create(proj)
  }
}
setupClass(CommercialDebugData);

class CommercialGuideState extends GuideState {
  init(proj) {
    super.init();
    this.steps = [
      {
        name: 'Set Location',
        isReady: () => {
          return proj.hasTouchedLocation;
        },
        pagePath: 'location',
      },
      {
        name: 'Create Spaces',
        isReady: () => {
          return proj.spaces.length > 0;
        },
        pagePath: 'spaces',
      },
      {
        name: 'Create Systems',
        isReady: () => {
          return proj.systems.length > 0;
        },
        pagePath: 'systems',
      },
      {
        name: 'Run Calculations',
        isReady: () => {
          let systemsWithResults =  proj.systems.filter(
            system => system.getResultsReadyAndSuccessful());
          return systemsWithResults.length > 0;
        },
        pagePath: 'results',
      },
    ]
  }
}
setupClass(CommercialGuideState);

export class CommercialProject extends Project {    
  init(id, units, opts) {
    super.init(id, units, opts);
  }

  setup() {
    this.type = ProjectTypes.Commercial;

    this.buildingAndEnv = BuildingAndEnv.create();
    // makeId: each new System needs a unique project id (System has no
    // createType(); see ComponentArray.createNew()).
    this.systems = new ComponentArray(System, {
      creationFunc: () => System.create(undefined, true),
    });
    this.spaces = new ComponentArray(Space);
    this.schedules = new ComponentArray(Schedule);

    this.wallTypes = new ComponentArray(WallType);
    this.roofTypes = new ComponentArray(RoofType);
    this.windowTypes = new ComponentArray(WindowType);
    this.interiorShadingTypes = new ComponentArray(InteriorShadingType);
    this.exteriorShadingTypes = new ComponentArray(ExteriorShadingType);
    this.doorTypes = new ComponentArray(DoorType);
    this.skylightTypes = new ComponentArray(SkylightType);
    this.bufferSpaceTypes = new ComponentArray(BufferSpaceType);

    // This is a library of custom components that the user can reuse
    this.componentsLibrary = ComponentsLibrary.create();

    this.windowsData = new WindowsData();
    this.timeSeriesData = new TimeSeriesDataTable(TimeSeriesData_Table);
    this.activityLevelsData = new MaterialDataTable(ActivityLevels_Table);
    this.luminairesData = new MaterialDataTable(LuminairesData_Table);

    this.loadResults = null;
    this.guide = CommercialGuideState.create(this);

    if (!this.workerProject) {
      this.debugData = CommercialDebugData.create(this);
    }
  }

  get serFields() {
    return [
      ...super.serFields,

      'guide',

      'buildingAndEnv',
      ser.arrayField('systems', () => { return System.create(); }),
      ser.arrayField('spaces', () => { return Space.create(); }),
      ser.arrayField('schedules', () => { return Schedule.create(); }),

      ser.arrayField('wallTypes', () => { return WallType.create(); }),
      ser.arrayField('roofTypes', () => { return RoofType.create(); }),
      ser.arrayField('windowTypes', () => { return WindowType.create(); }),
      ser.arrayField('interiorShadingTypes', () => { return InteriorShadingType.create(); }),
      ser.arrayField('exteriorShadingTypes', () => { return ExteriorShadingType.create(); }),
      ser.arrayField('doorTypes', () => { return DoorType.create(); }),
      ser.arrayField('skylightTypes', () => { return SkylightType.create(); }),
      ser.arrayField('bufferSpaceTypes', () => { return BufferSpaceType.create(); }),
      'componentsLibrary',
    ]
  }

  async onSetupNewProject() {
    console.log("Setting up new Commercial project...");

    this.shownWelcomeMessage = false;

    // Set the default value to Winnipeg MB, Canada
    // Do this here b/c we only want to do this on project create.
    await this.buildingAndEnv.locationData.setLocation({
      fullName: 'Winnipeg, MB. Canada',
      name: 'Winnipeg',
      path: ['Canada', 'MB', 'Winnipeg'],
    });

    // Add starter schedules
    let defaultSchedules = Schedule.createDefaultSchedules();
    extendArray(this.schedules, defaultSchedules);
  }

  async onPostReadFromJson() {
    console.log("Post read from JSON for Commercial project...");
    console.log("Reloading location data...");
    await this.buildingAndEnv.locationData.reloadData();
  }

  getLocationData() {
    return this.buildingAndEnv.locationData;
  }

  getSampleTemps() {
    let locationData = this.buildingAndEnv.locationData;
    return locationData.getSampleTemps();
  }

  getBaseRoute() {
    return `/building/${this.id}`;
  }

  getSystemWithId(id) {
    return this.systems.find((system) => system.id === id);
  }

  /**
   * Get a TypesSet containing all types in this project
   * @returns {TypesSet} A TypesSet with all the project's types
   */
  getTypesSet() {
    const typesSet = TypesSet.create();
    
    // Add all types from the project to the TypesSet
    for (const wallType of this.wallTypes) {
      typesSet.addType(wallType, 'wallTypes');
    }
    
    for (const roofType of this.roofTypes) {
      typesSet.addType(roofType, 'roofTypes');
    }

    for (const customMaterial of this.componentsLibrary.customMaterials) {
      typesSet.addType(customMaterial, 'customMaterials');
    }
    
    for (const windowType of this.windowTypes) {
      typesSet.addType(windowType, 'windowTypes');
    }
    
    for (const interiorShadingType of this.interiorShadingTypes) {
      typesSet.addType(interiorShadingType, 'interiorShadingTypes');
    }
    
    for (const exteriorShadingType of this.exteriorShadingTypes) {
      typesSet.addType(exteriorShadingType, 'exteriorShadingTypes');
    }
    
    for (const doorType of this.doorTypes) {
      typesSet.addType(doorType, 'doorTypes');
    }
    
    for (const skylightType of this.skylightTypes) {
      typesSet.addType(skylightType, 'skylightTypes');
    }
    
    for (const bufferSpaceType of this.bufferSpaceTypes) {
      typesSet.addType(bufferSpaceType, 'bufferSpaceTypes');
    }
    
    for (const schedule of this.schedules) {
      typesSet.addType(schedule, 'schedules');
    }
    
    for (const system of this.systems) {
      typesSet.addType(system, 'systems');
    }
    
    for (const space of this.spaces) {
      typesSet.addType(space, 'spaces');
    }

    return typesSet;
  }
};
setupClass(CommercialProject);
